import React, { useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import Spinner from '@atlaskit/spinner';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { token } from '@atlaskit/tokens';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { useIsInconsistentLicense } from './useIsInconsistentLicense.ts';

const i18n = defineMessages({
	licenseProcessingTitle: {
		id: 'inconsistent-license.flag.license-processing-title.ai-non-final',
		defaultMessage: 'You have a license processing',
		description:
			'Title text shown in a flag notification to inform the user that their Confluence license is currently being processed. Appears at the top of the flag notification.',
	},
	licenseProcessingDescription: {
		id: 'inconsistent-license.flag.license-processing-description.ai-non-final',
		defaultMessage:
			"While it's processing, your Confluence access is limited. We'll let you know when it's active and you can refresh with full access.",
		description:
			'Description text shown in a flag notification explaining that the user has limited Confluence access while their license is being processed, and that they will be notified when it becomes active. Appears below the flag title.',
	},
	licenseActiveTitle: {
		id: 'inconsistent-license.flag.license-active-title.ai-non-final',
		defaultMessage: 'Your Confluence license is now active',
		description:
			'Title text shown in a success flag notification to inform the user that their Confluence license has finished processing and is now active. Appears at the top of the flag notification.',
	},
	licenseActiveDescription: {
		id: 'inconsistent-license.flag.license-active-description.ai-non-final',
		defaultMessage:
			'Your license has finished processing. Refresh to experience full licensed access.',
		description:
			'Description text shown in a success flag notification telling the user their license processing is complete and they should refresh the page to get full access. Appears below the flag title.',
	},
	refreshActionLabel: {
		id: 'inconsistent-license.flag.refresh-action-label.ai-non-final',
		defaultMessage: 'Refresh',
		description:
			'Label for an action button shown in a flag notification. After a user clicks on this button, the page will reload so the user can access Confluence with their newly activated license.',
	},
});

const RELOAD = () => window?.location.reload();

const INCONSISTENT_LICENSE_WARNING_FLAG_ID = 'INCONSISTENT_LICENSE_WARNING_FLAG_ID';
const LICENSE_CONSISTENT_SUCCESS_FLAG_ID = 'LICENSE_CONSISTENT_SUCCESS_FLAG_ID';

const spinnerContainerStyles = css({
	position: 'relative',
});

const closeButtonCoverStyles = css({
	width: '32px',
	height: '32px',
	position: 'absolute',
	background: token('elevation.surface.overlay'),
	left: 300,
	top: -8,
	zIndex: 1,
	'@media (max-width: 560px)': {
		display: 'none',
	},
});

export const InconsistentLicenseFlagInternal = withFlags(({ flags }) => {
	const { formatMessage } = useIntl();

	const { isInconsistentLicense } = useIsInconsistentLicense();
	const [inconsistentLicenseWarningFlagShown, setInconsistentLicenseWarningFlagShown] =
		useState<boolean>(false);

	/** Show inconsistent license warning flag */
	useEffect(() => {
		if (!isInconsistentLicense) {
			return;
		}
		const showFlag = async () =>
			flags.showCustomFlag({
				isAutoDismiss: false,
				customIcon: <Spinner />,
				id: INCONSISTENT_LICENSE_WARNING_FLAG_ID,
				title: (
					<div css={spinnerContainerStyles}>
						<div css={closeButtonCoverStyles} />
						{formatMessage(i18n.licenseProcessingTitle)}
					</div>
				),
				description: formatMessage(i18n.licenseProcessingDescription),
				onClose: showFlag,
			});

		void showFlag();
		setInconsistentLicenseWarningFlagShown(true);
	}, [isInconsistentLicense, flags, formatMessage]);

	/** Show license consistent flag */
	useEffect(() => {
		const replaceWarningFlagWithSuccessFlag = async () => {
			await flags.hideFlag(INCONSISTENT_LICENSE_WARNING_FLAG_ID);
			await flags.showCustomFlag({
				isAutoDismiss: false,
				customIcon: <StatusSuccessIcon color={token('color.icon.success')} label="" />,
				id: LICENSE_CONSISTENT_SUCCESS_FLAG_ID,
				title: formatMessage(i18n.licenseActiveTitle),
				description: formatMessage(i18n.licenseActiveDescription),
				actions: [{ content: formatMessage(i18n.refreshActionLabel), onClick: RELOAD }],
			});
		};

		if (inconsistentLicenseWarningFlagShown && !isInconsistentLicense) {
			void replaceWarningFlagWithSuccessFlag();
		}
	}, [isInconsistentLicense, inconsistentLicenseWarningFlagShown, flags, formatMessage]);

	return null;
});

export const InconsistentLicenseFlagComponent = () => {
	const isSSR = useRenderServerPlaceholder();

	if (isSSR || !expValEquals('cc_fe_inconsistent_license_warning', 'isEnabled', true)) {
		return null;
	}

	return <InconsistentLicenseFlagInternal />;
};

export const InconsistentLicenseFlag = withTransparentErrorBoundary({
	attribution: Attribution.PERMISSIONS_EXPERIENCE,
})(InconsistentLicenseFlagComponent);
